/**
 * 神煞计算引擎（多语言版，问真主流口径规则驱动）
 * 规则单一事实源：shenshaRules.ts（转录自后端 wenzhen-shensha.json，78 条）
 * 命中算法：shenshaRulesEngine.ts（镜像后端 WenzhenShenshaEngine）
 * 多语言文案：SHENSHA_I18N（59 种，与后端 ShenshaI18nData 对齐）
 * 支持 zh-CN / zh-HK / en / ja / ko
 */

import { type FourPillars } from '../../baziCalc';
import { calcShenshaHits } from './shenshaRulesEngine';

export type ShenshaLevel = '大吉' | '吉' | '中' | '注意' | '化解';

export interface ShenshaItem {
  name: string;
  /** 神煞所在柱位标签数组（如 ['年柱','时柱']） */
  pillars: string[];
  level: ShenshaLevel;
  desc: string;
  resolve?: string;
  color: string;
  bg: string;
  border: string;
  /** 柱位影响力权重（0~100），年柱>日柱>月柱>时柱 */
  influenceScore: number;
}

// ── 多语言柱位名称 ───────────────────────────────────────────
type Lang = string;

function getPillarName(idx: number, lang: Lang): string {
  const maps: Record<string, string[]> = {
    'zh-CN': ['年柱', '月柱', '日柱', '时柱'],
    'zh-HK': ['年柱', '月柱', '日柱', '時柱'],
    'en':    ['Year', 'Month', 'Day', 'Hour'],
    'ja':    ['年柱', '月柱', '日柱', '時柱'],
    'ko':    ['년주', '월주', '일주', '시주'],
  };
  const arr = maps[lang] ?? maps['zh-CN'];
  return arr[idx] ?? '';
}
/** 天干整体标签（三奇贵人落 -1 时用），多语言 */
function getStemLabel(lang: Lang): string {
  switch (lang) {
    case 'en': return 'Stems';
    case 'ja': return '天干';
    case 'ko': return '천간';
    default:   return '天干';
  }
}
// ── 颜色辅助 ─────────────────────────────────────────────────
function buildColors(level: ShenshaLevel): { color: string; bg: string; border: string } {
  switch (level) {
    case '大吉': return { color: '#E8A020', bg: 'rgba(232,160,32,0.1)', border: 'rgba(232,160,32,0.3)' };
    case '吉':   return { color: '#7BC67E', bg: 'rgba(123,198,126,0.1)', border: 'rgba(123,198,126,0.3)' };
    case '中':   return { color: '#4A90D9', bg: 'rgba(74,144,217,0.1)', border: 'rgba(74,144,217,0.3)' };
    case '注意': return { color: '#8A7B91', bg: 'rgba(138,123,145,0.1)', border: 'rgba(138,123,145,0.3)' };
    case '化解': return { color: '#B79AC5', bg: 'rgba(183,154,197,0.1)', border: 'rgba(183,154,197,0.3)' };
    default:     return { color: '#8A7B91', bg: 'rgba(138,123,145,0.1)', border: 'rgba(138,123,145,0.3)' };
  }
}

// ── 多语言神煞名称 ───────────────────────────────────────────
const SHENSHA_NAMES: Record<string, Record<Lang, string>> = {
  '天乙贵人': { 'zh-CN': '天乙贵人', 'zh-HK': '天乙貴人', 'en': 'Tian Yi Noble', 'ja': '天乙貴人', 'ko': '천을귀인' },
  '文昌贵人': { 'zh-CN': '文昌贵人', 'zh-HK': '文昌貴人', 'en': 'Wen Chang Noble', 'ja': '文昌貴人', 'ko': '문창귀인' },
  '福星贵人': { 'zh-CN': '福星贵人', 'zh-HK': '福星貴人', 'en': 'Fortune Star', 'ja': '福星貴人', 'ko': '복성귀인' },
  '太极贵人': { 'zh-CN': '太极贵人', 'zh-HK': '太極貴人', 'en': 'Tai Ji Noble', 'ja': '太極貴人', 'ko': '태극귀인' },
  '天德贵人': { 'zh-CN': '天德贵人', 'zh-HK': '天德貴人', 'en': 'Heavenly Virtue', 'ja': '天徳貴人', 'ko': '천덕귀인' },
  '月德贵人': { 'zh-CN': '月德贵人', 'zh-HK': '月德貴人', 'en': 'Monthly Virtue', 'ja': '月徳貴人', 'ko': '월덕귀인' },
  '天德合':   { 'zh-CN': '天德合',   'zh-HK': '天德合',   'en': 'Heavenly Virtue Harmony', 'ja': '天徳合', 'ko': '천덕합' },
  '月德合':   { 'zh-CN': '月德合',   'zh-HK': '月德合',   'en': 'Monthly Virtue Harmony', 'ja': '月徳合', 'ko': '월덕합' },
  '天厨贵人': { 'zh-CN': '天厨贵人', 'zh-HK': '天廚貴人', 'en': 'Heavenly Kitchen', 'ja': '天厨貴人', 'ko': '천주귀인' },
  '十灵日':   { 'zh-CN': '十灵日',   'zh-HK': '十靈日',   'en': 'Ten Spirit Day', 'ja': '十霊日',   'ko': '십영일' },
  '十恶大败': { 'zh-CN': '十恶大败', 'zh-HK': '十惡大敗', 'en': 'Great Defeat', 'ja': '十悪大敗', 'ko': '십악대패' },
  '八专日':   { 'zh-CN': '八专日',   'zh-HK': '八專日',   'en': 'Eight Devotion', 'ja': '八専日', 'ko': '팔전일' },
  '六秀日':   { 'zh-CN': '六秀日',   'zh-HK': '六秀日',   'en': 'Six Excellence', 'ja': '六秀日', 'ko': '육수일' },
  '孤鸾煞':   { 'zh-CN': '孤鸾煞',   'zh-HK': '孤鸞煞',   'en': 'Lone Phoenix', 'ja': '孤鸞煞',   'ko': '고란살' },
  '词馆':     { 'zh-CN': '词馆',     'zh-HK': '詞館',     'en': 'Literary Hall','ja': '詞館',     'ko': '사관' },
  '金舆':     { 'zh-CN': '金舆',     'zh-HK': '金輿',     'en': 'Golden Chariot','ja': '金輿',    'ko': '금여' },
  '禄神':     { 'zh-CN': '禄神',     'zh-HK': '祿神',     'en': 'Prosperity Star','ja': '禄神',    'ko': '록신' },
  '国印贵人': { 'zh-CN': '国印贵人', 'zh-HK': '國印貴人', 'en': 'State Seal Noble','ja': '国印貴人','ko': '국인귀인' },
  '德秀贵人': { 'zh-CN': '德秀贵人', 'zh-HK': '德秀貴人', 'en': 'Virtue Talent Noble','ja': '徳秀貴人','ko': '덕수귀인' },
  '天罗地网': { 'zh-CN': '天罗地网', 'zh-HK': '天羅地網', 'en': "Heaven's Net",'ja': '天羅地網','ko': '천라지망' },
  '元辰':     { 'zh-CN': '元辰',     'zh-HK': '元辰',     'en': 'Origin Star',  'ja': '元辰',     'ko': '원신' },
  '劫煞':     { 'zh-CN': '劫煞',     'zh-HK': '劫煞',     'en': 'Robbery Star', 'ja': '劫煞',     'ko': '겁살' },
  '亡神':     { 'zh-CN': '亡神',     'zh-HK': '亡神',     'en': 'Perishing Spirit','ja': '亡神',   'ko': '망신' },
  '驿马':     { 'zh-CN': '驿马',     'zh-HK': '驛馬',     'en': 'Traveling Star', 'ja': '駅馬',     'ko': '역마' },
  '桃花':     { 'zh-CN': '桃花',     'zh-HK': '桃花',     'en': 'Peach Blossom',  'ja': '桃花',     'ko': '도화' },
  '羊刃':     { 'zh-CN': '羊刃',     'zh-HK': '羊刃',     'en': "Yang Blade",     'ja': '羊刃',     'ko': '양인' },
  '华盖':     { 'zh-CN': '华盖',     'zh-HK': '華蓋',     'en': 'Canopy Star',    'ja': '華蓋',     'ko': '화개' },
  '将星':     { 'zh-CN': '将星',     'zh-HK': '將星',     'en': 'General Star',   'ja': '将星',     'ko': '장성' },
  '魁罡日':   { 'zh-CN': '魁罡日',   'zh-HK': '魁罡日',   'en': 'Kui Gang',       'ja': '魁罡',     'ko': '괴강' },
  '孤辰':     { 'zh-CN': '孤辰',     'zh-HK': '孤辰',     'en': 'Lone Star',      'ja': '孤辰',     'ko': '고진' },
  '寡宿':     { 'zh-CN': '寡宿',     'zh-HK': '寡宿',     'en': 'Widow Star',     'ja': '寡宿',     'ko': '과숙' },
  '学堂':     { 'zh-CN': '学堂',     'zh-HK': '學堂',     'en': 'School Star',    'ja': '学堂',     'ko': '학당' },
  '红艳煞':   { 'zh-CN': '红艳煞',   'zh-HK': '紅艷煞',   'en': 'Red Beauty',     'ja': '紅艶',     'ko': '홍염' },
  '童子煞':   { 'zh-CN': '童子煞',   'zh-HK': '童子煞',   'en': 'Child Star Sha', 'ja': '童子煞',   'ko': '동자살' },
  '流霞':     { 'zh-CN': '流霞',     'zh-HK': '流霞',     'en': 'Flowing Rosy Cloud', 'ja': '流霞', 'ko': '류하' },
  '披麻':     { 'zh-CN': '披麻',     'zh-HK': '披麻',     'en': 'Mourning Cloth', 'ja': '披麻',     'ko': '피마' },
  '空亡':     { 'zh-CN': '空亡',     'zh-HK': '空亡',     'en': 'Void',           'ja': '空亡',     'ko': '공망' },
  '灾煞':     { 'zh-CN': '灾煞',     'zh-HK': '災煞',     'en': 'Calamity Star',  'ja': '災煞',     'ko': '재살' },
  '红鸾':     { 'zh-CN': '红鸾',     'zh-HK': '紅鸞',     'en': 'Red Phoenix',    'ja': '紅鸞',     'ko': '홍란' },
  '天喜':     { 'zh-CN': '天喜',     'zh-HK': '天喜',     'en': 'Heavenly Joy',   'ja': '天喜',     'ko': '천희' },
  '天医':     { 'zh-CN': '天医',     'zh-HK': '天醫',     'en': 'Heavenly Doctor', 'ja': '天医',     'ko': '천의' },
  '咸池':     { 'zh-CN': '咸池',     'zh-HK': '咸池',     'en': 'Xian Chi',       'ja': '咸池',     'ko': '함지' },
  '大耗':     { 'zh-CN': '大耗',     'zh-HK': '大耗',     'en': 'Great Loss',     'ja': '大耗',     'ko': '대모' },
  '血刃':     { 'zh-CN': '血刃',     'zh-HK': '血刃',     'en': 'Blood Blade',    'ja': '血刃',     'ko': '혈인' },
  '吊客':     { 'zh-CN': '吊客',     'zh-HK': '吊客',     'en': 'Mourning Guest', 'ja': '吊客',     'ko': '조객' },
  '丧门':     { 'zh-CN': '丧门',     'zh-HK': '喪門',     'en': 'Funeral Gate',   'ja': '喪門',     'ko': '상문' },
// ── 问真规则补齐（15 种，对齐后端 ShenshaI18nData.SHENSHA_NAMES）──
  '三奇贵人': { 'zh-CN': '三奇贵人', 'zh-HK': '三奇貴人', 'en': 'Three Wonders',      'ja': '三奇貴人', 'ko': '삼기귀인' },
  '天罗':     { 'zh-CN': '天罗',     'zh-HK': '天羅',     'en': 'Sky Net',            'ja': '天羅',     'ko': '천라' },
  '地网':     { 'zh-CN': '地网',     'zh-HK': '地網',     'en': 'Earth Net',          'ja': '地網',     'ko': '지망' },
  '阴差阳错': { 'zh-CN': '阴差阳错', 'zh-HK': '陰差陽錯', 'en': 'Yin-Yang Error',     'ja': '陰差陽錯', 'ko': '음차양착' },
  '天赦日':   { 'zh-CN': '天赦日',   'zh-HK': '天赦日',   'en': "Heaven's Pardon",    'ja': '天赦日',   'ko': '천사일' },
  '四废日':   { 'zh-CN': '四废日',   'zh-HK': '四廢日',   'en': 'Four Waste',         'ja': '四廃日',   'ko': '사폐일' },
  '勾绞煞':   { 'zh-CN': '勾绞煞',   'zh-HK': '勾絞煞',   'en': 'Entangle Star',      'ja': '勾絞煞',   'ko': '구교살' },
  '正学堂':   { 'zh-CN': '正学堂',   'zh-HK': '正學堂',   'en': 'True Academy',       'ja': '正学堂',   'ko': '정학당' },
  '正词馆':   { 'zh-CN': '正词馆',   'zh-HK': '正詞館',   'en': 'True Institute',     'ja': '正詞館',   'ko': '정사관' },
  '金神':     { 'zh-CN': '金神',     'zh-HK': '金神',     'en': 'Metal Spirit',       'ja': '金神',     'ko': '금신' },
  '拱禄':     { 'zh-CN': '拱禄',     'zh-HK': '拱祿',     'en': 'Embraced Prosperity', 'ja': '拱禄',   'ko': '공록' },
  '天转日':   { 'zh-CN': '天转日',   'zh-HK': '天轉日',   'en': 'Sky Turn',           'ja': '天転日',   'ko': '천전일' },
  '地转日':   { 'zh-CN': '地转日',   'zh-HK': '地轉日',   'en': 'Earth Turn',         'ja': '地転日',   'ko': '지전일' },
  '九丑日':   { 'zh-CN': '九丑日',   'zh-HK': '九丑日',   'en': 'Nine Ugliness',      'ja': '九丑日',   'ko': '구축일' },
  '飞刃':     { 'zh-CN': '飞刃',     'zh-HK': '飛刃',     'en': 'Flying Blade',       'ja': '飛刃',     'ko': '비인' },
};

// ── 柱位影响力权重（年柱 > 日柱 > 月柱 > 时柱）────────────────
const PILLAR_WEIGHT: Record<string, number> = {
  '年柱': 35, 'Year': 35, '년주': 35, '年柱_ja': 35,
  '日柱': 30, 'Day': 30, '일주': 30, '日柱_ja': 30,
  '月柱': 20, 'Month': 20, '월주': 20, '月柱_ja': 20,
  '时柱': 15, 'Hour': 15, '시주': 15, '時柱': 15,
};

const LEVEL_BONUS: Record<ShenshaLevel, number> = {
  '大吉': 5,
  '吉': 3,
  '中': 0,
  '注意': -2,
  '化解': -1,
};

/** 等级排序权重（大吉→吉→中→注意→化解），与后端 ShenshaI18nData.levelOrderComparator 一致 */
const LEVEL_WEIGHT: Record<ShenshaLevel, number> = {
  '大吉': 0,
  '吉': 1,
  '中': 2,
  '注意': 3,
  '化解': 4,
};

/** 根据柱位与等级计算影响力得分 (0~100) */
function calcInfluenceScore(pillars: string[], level: ShenshaLevel): number {
  let base = 0;
  for (const p of pillars) {
    base += PILLAR_WEIGHT[p] || 15;
  }
  const bonus = LEVEL_BONUS[level] || 0;
  return Math.min(100, Math.max(0, base + bonus));
}

function getShenshaName(key: string, lang: Lang): string {
  return SHENSHA_NAMES[key]?.[lang] ?? SHENSHA_NAMES[key]?.['zh-CN'] ?? key;
}
// ── 神煞默认等级（对齐后端 ShenshaI18nData.SHENSHA_LEVEL）────
const SHENSHA_LEVEL: Record<string, ShenshaLevel> = {
  '天乙贵人': '吉',   '文昌贵人': '吉',
  '福星贵人': '吉',   '太极贵人': '吉',
  '天德贵人': '大吉', '月德贵人': '吉',
  '天厨贵人': '吉',   '十灵日': '吉',
  '孤鸾煞': '注意',   '词馆': '吉',
  '金舆': '大吉',     '元辰': '注意',
  '禄神': '吉',       '国印贵人': '吉',
  '德秀贵人': '吉',   '天德合': '吉',
  '月德合': '吉',     '童子煞': '注意',
  '流霞': '中',       '披麻': '化解',
  '空亡': '中',
  '劫煞': '注意',     '亡神': '注意',
  '驿马': '中',       '桃花': '中',
  '羊刃': '中',       '华盖': '中',
  '将星': '吉',       '魁罡日': '中',
  '孤辰': '注意',     '寡宿': '化解',
  '学堂': '吉',       '红艳煞': '中',
  '灾煞': '注意',     '红鸾': '吉',
  '天喜': '吉',       '天医': '吉',
  '咸池': '注意',     '大耗': '注意',
  '血刃': '注意',     '吊客': '化解',
  '丧门': '化解',
  '三奇贵人': '大吉', '天罗地网': '注意',
  '天罗': '注意',     '地网': '注意',
  '十恶大败': '注意', '阴差阳错': '注意',
  '天赦日': '吉',     '六秀日': '吉',
  '四废日': '注意',   '勾绞煞': '注意',
  '正学堂': '吉',     '正词馆': '吉',
  '金神': '中',       '拱禄': '吉',
  '天转日': '注意',   '地转日': '注意',
  '八专日': '中',     '九丑日': '注意',
  '飞刃': '注意',
};

// ── 多语言等级标签 ───────────────────────────────────────────
const LEVEL_LABELS: Record<ShenshaLevel, Record<Lang, string>> = {
  '大吉': { 'zh-CN': '大吉', 'zh-HK': '大吉', 'en': 'Great', 'ja': '大吉', 'ko': '대길' },
  '吉':   { 'zh-CN': '吉',   'zh-HK': '吉',   'en': 'Lucky', 'ja': '吉',   'ko': '길' },
  '中':   { 'zh-CN': '中',   'zh-HK': '中',   'en': 'Neutral','ja': '中',  'ko': '중' },
  '注意': { 'zh-CN': '注意', 'zh-HK': '注意', 'en': 'Caution','ja': '注意','ko': '주의' },
  '化解': { 'zh-CN': '化解', 'zh-HK': '化解', 'en': 'Resolve','ja': '化解','ko': '화해' },
};

export function getShenshaLevelLabel(level: ShenshaLevel, lang: Lang): string {
  return LEVEL_LABELS[level]?.[lang] ?? level;
}

// ── 多语言描述生成 ───────────────────────────────────────────
type DescFn = (pillars: string, cnt: number, extra?: string) => string;

interface ShenshaI18nEntry {
  desc: DescFn;
  resolve?: DescFn;
}

type ShenshaI18nMap = Record<string, Record<Lang, ShenshaI18nEntry>>;

const SHENSHA_I18N: ShenshaI18nMap = {
  '天乙贵人': {
    'zh-CN': {
      desc: (p, cnt) => cnt >= 2
        ? `天乙贵人${cnt}现，贵气极旺，一生逢凶化吉，贵人提携不断，危难之时必有援手。遇到困境时，不妨主动求助，人脉将是最大的财富。`
        : `天乙贵人坐${p}，命中有贵人相助，处事顺畅，遇事多有转机。保持善缘，贵人自然亲近。`,
    },
    'zh-HK': {
      desc: (p, cnt) => cnt >= 2
        ? `天乙貴人${cnt}現，貴氣極旺，一生逢凶化吉，貴人提攜不斷，危難之時必有援手。遇到困境時，不妨主動求助，人脈將是最大的財富。`
        : `天乙貴人坐${p}，命中有貴人相助，處事順暢，遇事多有轉機。保持善緣，貴人自然親近。`,
    },
    'en': {
      desc: (p, cnt) => cnt >= 2
        ? `Tian Yi Noble appears ${cnt} times — extremely auspicious. Noble helpers appear throughout life, turning misfortune into luck. Seek help proactively; your network is your greatest asset.`
        : `Tian Yi Noble sits in the ${p} pillar. Noble helpers appear in times of need, bringing smooth progress and fortunate turns. Cultivate good relationships and benefactors will naturally draw near.`,
    },
    'ja': {
      desc: (p, cnt) => cnt >= 2
        ? `天乙貴人が${cnt}つ現れ、貴気が極めて旺盛です。一生を通じて凶を吉に変え、貴人の援助が絶えません。困難な時は積極的に助けを求めましょう。人脈が最大の財産となります。`
        : `天乙貴人が${p}に坐します。命中に貴人の助けがあり、物事が順調に進み、困難な時に転機が訪れます。善縁を保てば、貴人は自然と近づいてきます。`,
    },
    'ko': {
      desc: (p, cnt) => cnt >= 2
        ? `천을귀인이 ${cnt}개 나타나 귀기가 매우 왕성합니다. 평생 흉을 길로 바꾸며 귀인의 도움이 끊이지 않습니다. 어려울 때 적극적으로 도움을 구하세요. 인맥이 가장 큰 재산입니다。`
        : `천을귀인이 ${p}에 앉아 있습니다. 명중에 귀인의 도움이 있어 일이 순조롭고 어려울 때 전기가 찾아옵니다. 좋은 인연을 유지하면 귀인이 자연스럽게 가까워집니다。`,
    },
  },
  '文昌贵人': {
    'zh-CN': { desc: (p) => `文昌贵人坐${p}，聪明好学，文思泉涌，记忆力强，利于学业考试与文艺创作。` },
    'zh-HK': { desc: (p) => `文昌貴人坐${p}，聰明好學，文思泉湧，記憶力強，利於學業考試與文藝創作。` },
    'en':    { desc: (p) => `Wen Chang Noble in the ${p} pillar — intelligent and studious, with excellent memory and creative thinking. Favorable for academic exams and artistic pursuits.` },
    'ja':    { desc: (p) => `文昌貴人が${p}に坐します。聡明で学習好き、文才が豊かで記憶力が強く、学業試験や文芸創作に有利です。` },
    'ko':    { desc: (p) => `문창귀인이 ${p}에 앉아 있습니다. 총명하고 학습을 좋아하며 문재가 풍부하고 기억력이 강해 학업 시험과 문예 창작에 유리합니다。` },
  },
  '福星贵人': {
    'zh-CN': { desc: (p) => `福星贵人坐${p}，一生福气深厚，衣食无忧，遇事常有转机，晚年尤佳。` },
    'zh-HK': { desc: (p) => `福星貴人坐${p}，一生福氣深厚，衣食無憂，遇事常有轉機，晚年尤佳。` },
    'en':    { desc: (p) => `Fortune Star Noble in the ${p} pillar — deep blessings throughout life, free from worry about food and clothing. fortunate turns appear in adversity, especially in later years.` },
    'ja':    { desc: (p) => `福星貴人が${p}に坐します。一生を通じて福気が深く、衣食に困らず、困難な時に転機が訪れ、晩年に特に良いです。` },
    'ko':    { desc: (p) => `복성귀인이 ${p}에 앉아 있습니다. 평생 복이 깊고 의식에 걱정이 없으며 어려울 때 전기가 찾아와 만년에 특히 좋습니다。` },
  },
  '太极贵人': {
    'zh-CN': { desc: (p) => `太极贵人坐${p}，悟性强，善于思考宇宙人生哲理，对玄学、宗教、哲学有浓厚兴趣与天赋。` },
    'zh-HK': { desc: (p) => `太極貴人坐${p}，悟性強，善於思考宇宙人生哲理，對玄學、宗教、哲學有濃厚興趣與天賦。` },
    'en':    { desc: (p) => `Tai Ji Noble in the ${p} pillar — strong insight, inclined to contemplate the universe and philosophy. Drawn to metaphysics, religion, and philosophy with natural talent.` },
    'ja':    { desc: (p) => `太極貴人が${p}に坐します。悟りが深く、宇宙と人生の哲理を考えることを好み、玄学・宗教・哲学に強い興味と才能があります。` },
    'ko':    { desc: (p) => `태극귀인이 ${p}에 앉아 있습니다. 깨달음이 강하고 우주와 인생의 철학을 생각하는 것을 좋아하며 현학·종교·철학에 강한 흥미와 재능이 있습니다。` },
  },
  '天德贵人': {
    'zh-CN': { desc: (p) => `天德贵人坐${p}，德泽深厚，心地善良，逢凶化吉，遇难呈祥，一生多得阴德庇护。` },
    'zh-HK': { desc: (p) => `天德貴人坐${p}，德澤深厚，心地善良，逢凶化吉，遇難呈祥，一生多得陰德庇護。` },
    'en':    { desc: (p) => `Heavenly Virtue Noble in the ${p} pillar — deep moral grace, kind-hearted. Turns misfortune into luck and danger into blessing, protected by hidden merit throughout life.` },
    'ja':    { desc: (p) => `天徳貴人が${p}に坐します。徳が深く心が善良で、凶を吉に変え困難を幸いに変え、一生を通じて陰徳の加護を受けます。` },
    'ko':    { desc: (p) => `천덕귀인이 ${p}에 앉아 있습니다. 덕이 깊고 마음이 착하며 흉을 길로 바꾸고 어려움을 복으로 변하게 하며 평생 음덕의 보호를 받습니다。` },
  },
  '月德贵人': {
    'zh-CN': { desc: (p) => `月德贵人坐${p}，心地善良，品德高尚，多得长辈与女性贵人相助，逢凶化吉。` },
    'zh-HK': { desc: (p) => `月德貴人坐${p}，心地善良，品德高尚，多得長輩與女性貴人相助，逢凶化吉。` },
    'en':    { desc: (p) => `Monthly Virtue Noble in the ${p} pillar — kind and virtuous. Receives help from elders and female benefactors, turning misfortune into luck.` },
    'ja':    { desc: (p) => `月徳貴人が${p}に坐します。心が善良で品德が高く、長輩や女性の貴人から多くの助けを受け、凶を吉に変えます。` },
    'ko':    { desc: (p) => `월덕귀인이 ${p}에 앉아 있습니다. 마음이 착하고 품덕이 높으며 장辈와 여성 귀인에게 많은 도움을 받아 흉을 길로 바꿉니다。` },
  },
  '天厨贵人': {
    'zh-CN': { desc: (p) => `天厨贵人坐${p}，一生衣食无忧，食禄丰厚，善于烹饪或美食相关事业，晚年福寿双全。` },
    'zh-HK': { desc: (p) => `天廚貴人坐${p}，一生衣食無憂，食祿豐厚，善於烹飪或美食相關事業，晚年福壽雙全。` },
    'en':    { desc: (p) => `Heavenly Kitchen Noble in the ${p} pillar — free from worry about food and clothing. Rich in sustenance, skilled at cooking or food-related ventures, blessed in later years.` },
    'ja':    { desc: (p) => `天厨貴人が${p}に坐します。一生を通じて衣食に困らず、食禄が豊かで、料理や美食関連の事業に長け、晩年に福寿双全です。` },
    'ko':    { desc: (p) => `천주귀인이 ${p}에 앉아 있습니다. 평생 의식에 걱정이 없고 식록이 풍부하며 요리나 미식 관련 사업에 능하고 만년에 복수가 함께합니다。` },
  },
  '十灵日': {
    'zh-CN': { desc: () => `日柱为十灵日，天性聪颖，悟性极高，直觉敏锐，对玄学、命理、宗教有天然亲近感与天赋。` },
    'zh-HK': { desc: () => `日柱為十靈日，天性聰穎，悟性極高，直覺敏銳，對玄學、命理、宗教有天然親近感與天賦。` },
    'en':    { desc: () => `Ten Spirit Day — naturally bright, extremely perceptive, with keen intuition. Drawn to metaphysics, astrology, and religion with innate affinity and talent.` },
    'ja':    { desc: () => `日柱が十霊日です。天性が聡明で悟りが極めて深く、直感が鋭く、玄学・命理・宗教に対して天然の親近感と才能があります。` },
    'ko':    { desc: () => `일주가 십영일입니다. 천성이 총명하고 깨달음이 매우 깊으며 직감이 예민하고 현학·명리·종교에 대해 천연의 친근감과 재능이 있습니다。` },
  },
  '孤鸾煞': {
    'zh-CN': {
      desc: () => `孤鸾煞坐日柱，感情路上多有波折，婚姻宜晚不宜早，性格独立自主。`,
      resolve: () => '宜晚婚，择偶时注重性格契合，培养共同兴趣，婚姻中多沟通包容',
    },
    'zh-HK': {
      desc: () => `孤鸞煞坐日柱，感情路上多有波折，婚姻宜晚不宜早，性格獨立自主。`,
      resolve: () => '宜晚婚，擇偶時注重性格契合，培養共同興趣，婚姻中多溝通包容',
    },
    'en': {
      desc: () => `Lone Phoenix in the Day pillar — a bumpy road in love. Marriage is better later than sooner, with an independent and self-reliant character.`,
      resolve: () => 'Marry later rather than earlier. Focus on character compatibility when choosing a partner, cultivate shared interests, and communicate openly in marriage.',
    },
    'ja': {
      desc: () => `孤鸞煞が日柱に坐します。恋愛の道に多くの波乱があり、結婚は早婚より晩婚が望ましく、性格が独立して自主的です。`,
      resolve: () => '晩婚が望ましく、伴侶を選ぶ時は性格の契合を重視し、共通の趣味を培い、結婚生活中はコミュニケーションと包容を大切にしましょう。',
    },
    'ko': {
      desc: () => `고란살이 일주에 앉아 있습니다. 사랑의 길에 많은 파란이 있고 결혼은 조혼보다 만혼이 좋으며 성격이 독립적이고 자주적입니다。`,
      resolve: () => '만혼이 좋고 배우자를 선택할 때 성격의 조화를 중시하며 공통의 취미를 기르고 결혼 생활에서 소통과 포용을 많이 하세요。',
    },
  },
  '词馆': {
    'zh-CN': { desc: (p) => `词馆坐${p}，文采出众，口才了得，适合从事文学、教育、传媒、演讲等需要表达力的行业。` },
    'zh-HK': { desc: (p) => `詞館坐${p}，文采出眾，口才了得，適合從事文學、教育、傳媒、演講等需要表達力的行業。` },
    'en':    { desc: (p) => `Literary Hall in the ${p} pillar — outstanding literary talent and eloquence. Suited for literature, education, media, public speaking, or any expressive profession.` },
    'ja':    { desc: (p) => `詞館が${p}に坐します。文才が優れ、弁舌が立ち、文学・教育・マスコミ・演説など表現力を必要とする業界に適しています。` },
    'ko':    { desc: (p) => `사관이 ${p}에 앉아 있습니다. 문재가 뛰어나고 언변력이 좋아 문학·교육·미디어·연설 등 표현력이 필요한 업계에 적합합니다。` },
  },
  '金舆': {
    'zh-CN': { desc: (p) => `金舆坐${p}，一生富贵荣华，出行有车马之便，社会地位显赫，财运亨通。` },
    'zh-HK': { desc: (p) => `金輿坐${p}，一生富貴榮華，出行有車馬之便，社會地位顯赫，財運亨通。` },
    'en':    { desc: (p) => `Golden Chariot in the ${p} pillar — a life of wealth and honor. Blessed with convenient travel and transportation, prominent social status, and prosperous fortune.` },
    'ja':    { desc: (p) => `金輿が${p}に坐します。一生富貴栄華で、出行に車馬の便利があり、社会的地位が顕著で財運が亨通です。` },
    'ko':    { desc: (p) => `금여가 ${p}에 앉아 있습니다. 평생 부귀영화하며 출행에 차마의 편의가 있고 사회적 지위가 뚜렷하며 재운이 형통합니다。` },
  },
  '禄神': {
    'zh-CN': { desc: (p) => `禄神坐${p}，日主得禄，有根气与衣禄之象，利稳定发展与资源积累。` },
    'zh-HK': { desc: (p) => `祿神坐${p}，日主得祿，有根氣與衣祿之象，利穩定發展與資源積累。` },
    'en': { desc: (p) => `Prosperity Star in the ${p} pillar — the Day Master has rooted support, favoring stable growth and resource accumulation.` },
    'ja': { desc: (p) => `禄神が${p}に坐し、日主に根と衣禄の象があります。安定発展と資源蓄積に有利です。` },
    'ko': { desc: (p) => `록신이 ${p}에 있어 일간의 뿌리와 의록의 상이 있으며 안정 발전과 자원 축적에 유리합니다。` },
  },
  '国印贵人': {
    'zh-CN': { desc: (p) => `国印贵人坐${p}，主规则意识、文书印信与组织资源，利学业、证照、体制与管理事务。` },
    'zh-HK': { desc: (p) => `國印貴人坐${p}，主規則意識、文書印信與組織資源，利學業、證照、體制與管理事務。` },
    'en': { desc: (p) => `State Seal Noble in the ${p} pillar — supports credentials, documentation, institutions, management, and organized resources.` },
    'ja': { desc: (p) => `国印貴人が${p}に坐し、文書・資格・組織資源・管理事務に利があります。` },
    'ko': { desc: (p) => `국인귀인이 ${p}에 있어 문서, 자격, 조직 자원과 관리 업무에 유리합니다。` },
  },
  '德秀贵人': {
    'zh-CN': { desc: (p) => `德秀贵人坐${p}，气质清秀，才德兼备，利文艺、学业、表达与品格声誉。` },
    'zh-HK': { desc: (p) => `德秀貴人坐${p}，氣質清秀，才德兼備，利文藝、學業、表達與品格聲譽。` },
    'en': { desc: (p) => `Virtue Talent Noble in the ${p} pillar — refined temperament, talent and virtue, favorable for study, arts, expression, and reputation.` },
    'ja': { desc: (p) => `徳秀貴人が${p}に坐し、才徳を兼ね、学業・文芸・表現・声誉に利があります。` },
    'ko': { desc: (p) => `덕수귀인이 ${p}에 있어 재능과 덕을 겸비하고 학업, 예술, 표현과 명예에 유리합니다。` },
  },
  '天罗地网': {
    'zh-CN': { desc: (p) => `天罗地网见${p}，提示行事宜留余地，注意规则边界与情绪压力，稳妥推进更佳。`, resolve: () => '遇到复杂事务先理清规则与责任边界，避免急躁推进，保持作息与情绪稳定' },
    'zh-HK': { desc: (p) => `天羅地網見${p}，提示行事宜留餘地，注意規則邊界與情緒壓力，穩妥推進更佳。`, resolve: () => '遇到複雜事務先理清規則與責任邊界，避免急躁推進，保持作息與情緒穩定' },
    'en': { desc: (p) => `Heaven Net in the ${p} pillar — move carefully, respect boundaries and rules, and manage emotional pressure with patience.`, resolve: () => 'Clarify rules and responsibilities before acting, avoid rushing, and keep routines and emotions steady.' },
    'ja': { desc: (p) => `天羅地網が${p}に見えます。規則境界と心理的圧力に注意し、慎重に進めると良いでしょう。`, resolve: () => '複雑な事柄では規則と責任範囲を先に整理し、焦らず安定して進めましょう。' },
    'ko': { desc: (p) => `천라지망이 ${p}에 보입니다. 규칙과 경계, 정서적 압력에 유의하고 신중히 진행하는 것이 좋습니다。`, resolve: () => '복잡한 일은 규칙과 책임 범위를 먼저 정리하고 조급함을 피하며 생활 리듬과 감정을 안정시키세요。' },
  },
  '元辰': {
    'zh-CN': {
      desc: (p) => `元辰坐${p}，提示近期理财方面可多加留意，做好收支规划更为稳妥。此为理财提醒之象，非定论。`,
      resolve: () => '宜稳健理财，避免高风险投资，注意合同细节与人际交往中的沟通',
    },
    'zh-HK': {
      desc: (p) => `元辰坐${p}，提示近期理財方面可多加留意，做好收支規劃更為穩妥。此為理財提醒之象，非定論。`,
      resolve: () => '宜穩健理財，避免高風險投資，注意合同細節與人際交往中的溝通',
    },
    'en': {
      desc: (p) => `Origin Star in the ${p} pillar — a gentle reminder to pay extra attention to financial planning during this period. Keep a balanced budget and stay mindful of spending.`,
      resolve: () => 'Practice prudent financial management, avoid high-risk investments, and pay attention to contract details and interpersonal communication.',
    },
    'ja': {
      desc: (p) => `元辰が${p}に坐します。この時期は財務計画に少し意識を向け、収支のバランスを整えると安心です。これは理財へのやさしい注意喚起であり、固定された結果ではありません。`,
      resolve: () => '堅実な理財を心がけ、高リスク投資を避け、契約の細部と人間関係のコミュニケーションに注意してください。',
    },
    'ko': {
      desc: (p) => `원신이 ${p}에 앉아 있습니다. 이 시기에는 재무 계획에 조금 더 신경 쓰고 수입과 지출의 균형을 맞추면 좋습니다。이는 재정 관리를 위한 부드러운 알림이며 고정된 결과가 아닙니다。`,
      resolve: () => '건전한 재무 관리를 하고 고위험 투자를 피하며 계약 세부사항과 인간관계 소통에 주의하세요。',
    },
  },
  '劫煞': {
    'zh-CN': {
      desc: (p) => `劫煞坐${p}，提示在此时期内理财与人际方面多加留意，出行前做好规划更为稳妥。此为提醒调整节奏之象，非定论。`,
      resolve: () => '多行善举积累正能量，保持低调稳重，出行前做好准备，重要文件财物妥善保管',
    },
    'zh-HK': {
      desc: (p) => `劫煞坐${p}，提示在此時期內理財與人際方面多加留意，出行前做好規劃更為穩妥。此為提醒調整節奏之象，非定論。`,
      resolve: () => '多行善舉積累正能量，保持低調穩重，出行前做好準備，重要文件財物妥善保管',
    },
    'en': {
      desc: (p) => `Robbery Star in the ${p} pillar — a time to pay extra attention to finances and interpersonal matters. Plan ahead before traveling and stay mindful of your surroundings.`,
      resolve: () => 'Build positive connections through kindness, keep a low profile, stay safe when traveling, and keep important documents and valuables secure.',
    },
    'ja': {
      desc: (p) => `劫煞が${p}に坐します。この時期は金銭管理と人間関係に少し注意を向けると良いでしょう。出行前に計画を整え、周囲の状況に気を配ることで安心です。`,
      resolve: () => '善い行いを積んで徳を高め、控えめに行動し、出行時は安全に注意し、重要な書類や財物は適切に保管してください。',
    },
    'ko': {
      desc: (p) => `겁살이 ${p}에 앉아 있습니다. 이 시기에는 재무와 인간관계에 조금 더 주의를 기울이면 좋습니다. 출행 전 계획을 잘 세우고 주변 상황을 살피면 안심됩니다。`,
      resolve: () => '선행을 많이 하여 덕을 쌓고 조용히 행동하며 출행 시 안전에 주의하고 중요한 서류와 재물은 적절히 보관하세요。',
    },
  },
  '亡神': {
    'zh-CN': {
      desc: (p) => `亡神坐${p}，提示近期精神状态与做事节奏方面可多加调节，保持冷静与耐心更能顺利推进。此为提醒调整心态之象，非定论。`,
      resolve: () => '修身养性，保持正念，遇事冷静从容，避免在情绪波动时做重要决策',
    },
    'zh-HK': {
      desc: (p) => `亡神坐${p}，提示近期精神狀態與做事節奏方面可多加調節，保持冷靜與耐心更能順利推進。此為提醒調整心態之象，非定論。`,
      resolve: () => '修身養性，保持正念，遇事冷靜從容，避免在情緒波動時做重要決策',
    },
    'en': {
      desc: (p) => `Perishing Spirit in the ${p} pillar — a gentle reminder to pay attention to your mental state and pace of work. Stay calm and patient for smoother progress. This is a signal to adjust your mindset, not a fixed outcome.`,
      resolve: () => 'Cultivate character and nurture your spirit. Maintain right mindfulness, stay composed in adversity, and avoid making important decisions during emotional turbulence.',
    },
    'ja': {
      desc: (p) => `亡神が${p}に坐します。この時期は精神状態と仕事のペースに少し気を配り、冷静さと忍耐を保つことでより円滑に進みます。これは心の調整を促すやさしいサインであり、固定された結果ではありません。`,
      resolve: () => '身性を修め養性を保ち、正しい心を維持し、困難に冷静に対処し、感情が揺れている時は重要な決断を避けてください。',
    },
    'ko': {
      desc: (p) => `망신이 ${p}에 앉아 있습니다. 이 시기에는 정신 상태와 일의 페이스에 조금 더 신경 쓰고 침착함과 인내심을 유지하면 더 원활하게 진행됩니다。이는 마음의 조정을 위한 부드러운 신호이며 고정된 결과가 아닙니다。`,
      resolve: () => '수신양성을 하고 바른 마음을 유지하며 어려움에 침착하게 대처하고 감정이 흔들릴 때는 중요한 결정을 피하세요。',
    },
  },
  '驿马': {
    'zh-CN': { desc: (p) => `驿马坐${p}，一生奔波流动，适合与出行、迁移、外贸相关的工作，动中求财，越动越旺。` },
    'zh-HK': { desc: (p) => `驛馬坐${p}，一生奔波流動，適合與出行、遷移、外貿相關的工作，動中求財，越動越旺。` },
    'en':    { desc: (p) => `Traveling Star in the ${p} pillar — a life of movement and change. Suited for travel, relocation, or international trade. Fortune grows through action and mobility.` },
    'ja':    { desc: (p) => `駅馬が${p}に坐します。一生を通じて奔走し流動します。出行・移転・貿易関連の仕事に適しており、動くほど運勢が旺盛になります。` },
    'ko':    { desc: (p) => `역마가 ${p}에 앉아 있습니다. 평생 분주하게 움직이며 출행·이주·무역 관련 일에 적합합니다. 움직일수록 운세가 왕성해집니다。` },
  },
  '桃花': {
    'zh-CN': { desc: (p) => `桃花坐${p}，魅力出众，人缘极佳，异性缘旺。桃花是人际魅力的体现，善加运用可为事业加分，但需专注真心感情。` },
    'zh-HK': { desc: (p) => `桃花坐${p}，魅力出眾，人緣極佳，異性緣旺。桃花是人際魅力的體現，善加運用可為事業加分，但需專注真心感情。` },
    'en':    { desc: (p) => `Peach Blossom in the ${p} pillar — charming, popular, and magnetically attractive. This star enhances social appeal and can boost career through relationships. Focus on genuine connections.` },
    'ja':    { desc: (p) => `桃花が${p}に坐します。魅力的で人気があり、異性縁が旺盛です。桃花は人際的な魅力の表れで、うまく活用すれば事業にもプラスになりますが、真心の感情に集中することが大切です。` },
    'ko':    { desc: (p) => `도화가 ${p}에 앉아 있습니다. 매력적이고 인기가 많으며 이성 인연이 왕성합니다. 도화는 인간적 매력의 표현으로 잘 활용하면 사업에도 도움이 되지만 진심 어린 감정에 집중해야 합니다。` },
  },
  '羊刃': {
    'zh-CN': {
      desc: (p, cnt) => cnt >= 2
        ? `羊刃多现，意志坚定、行事果断，有突破困境的勇气与魄力。需注意防止行事过于冲动，凡事三思而后行，才能将锋锐化为成就。`
        : `羊刃坐${p}，个性果敢，意志力强，竞争中能坚持到底。`,
      resolve: (_, cnt) => cnt >= 2 ? '宜习武、从事竞技或需要魄力的行业，以刃制刃' : undefined as unknown as string,
    },
    'zh-HK': {
      desc: (p, cnt) => cnt >= 2
        ? `羊刃多現，意志堅定、行事果斷，有突破困境的勇氣與魄力。需注意防止行事過於衝動，凡事三思而後行，才能將鋒銳化為成就。`
        : `羊刃坐${p}，個性果敢，意志力強，競爭中能堅持到底。`,
      resolve: (_, cnt) => cnt >= 2 ? '宜習武、從事競技或需要魄力的行業，以刃制刃' : undefined as unknown as string,
    },
    'en': {
      desc: (p, cnt) => cnt >= 2
        ? `Yang Blade appears multiple times — strong-willed and decisive, with the courage to break through obstacles. Guard against impulsiveness; think before acting to channel this sharp energy into achievement.`
        : `Yang Blade in the ${p} pillar — bold character, strong willpower, able to persist through competition.`,
      resolve: (_, cnt) => cnt >= 2 ? 'Channel this energy into martial arts, competitive sports, or fields requiring boldness and decisiveness.' : undefined as unknown as string,
    },
    'ja': {
      desc: (p, cnt) => cnt >= 2
        ? `羊刃が複数現れ、意志が堅固で行動が果断です。困境を突破する勇気と魄力があります。衝動的な行動を避け、よく考えてから行動することで、鋭さを成就に変えられます。`
        : `羊刃が${p}に坐します。個性が果敢で意志力가 강하며 경쟁 속에서도 끝까지 버틸 수 있습니다。`,
      resolve: (_, cnt) => cnt >= 2 ? '武道・競技・魄力が必要な分野に従事することで、刃의 힘을 올바르게 발휘할 수 있습니다。' : undefined as unknown as string,
    },
    'ko': {
      desc: (p, cnt) => cnt >= 2
        ? `양인이 여러 개 나타나 의지가 굳고 행동이 과단합니다. 어려움을 돌파하는 용기와 기백이 있습니다. 충동적인 행동을 피하고 신중하게 생각한 후 행동하면 날카로움을 성취로 바꿀 수 있습니다。`
        : `양인이 ${p}에 앉아 있습니다. 개성이 과감하고 의지력이 강하며 경쟁 속에서도 끝까지 버틸 수 있습니다。`,
      resolve: (_, cnt) => cnt >= 2 ? '무술·경기·기백이 필요한 분야에 종사하면 양인의 힘을 올바르게 발휘할 수 있습니다。' : undefined as unknown as string,
    },
  },
  '华盖': {
    'zh-CN': { desc: (p) => `华盖坐${p}，聪明多才，艺术感强，喜钻研学问。华盖主孤，宜发挥学术与艺术天赋，精神世界丰富。` },
    'zh-HK': { desc: (p) => `華蓋坐${p}，聰明多才，藝術感強，喜鑽研學問。華蓋主孤，宜發揮學術與藝術天賦，精神世界豐富。` },
    'en':    { desc: (p) => `Canopy Star in the ${p} pillar — intelligent and multi-talented with strong artistic sensibility. This star favors solitary study and spiritual depth. Develop academic and artistic gifts for a rich inner life.` },
    'ja':    { desc: (p) => `華蓋が${p}に坐します。聡明で多才、芸術的センスが強く、学問の探求を好みます。華蓋は孤独を主り、学術と芸術の才能を発揮することで精神世界が豊かになります。` },
    'ko':    { desc: (p) => `화개가 ${p}에 앉아 있습니다. 총명하고 다재다능하며 예술적 감각이 강하고 학문 탐구를 좋아합니다. 화개는 고독을 주관하므로 학술과 예술 재능을 발휘하면 정신세계가 풍요로워집니다。` },
  },
  '将星': {
    'zh-CN': { desc: (p) => `将星坐${p}，有领导才能，权威感强，人群中自然能聚集人望，适合统筹管理工作。` },
    'zh-HK': { desc: (p) => `將星坐${p}，有領導才能，權威感強，人群中自然能聚集人望，適合統籌管理工作。` },
    'en':    { desc: (p) => `General Star in the ${p} pillar — natural leadership ability, commanding presence, and the power to rally people. Well-suited for management and organizational roles.` },
    'ja':    { desc: (p) => `将星が${p}に坐します。リーダーシップの才能があり、権威感が強く、人々の中で自然と人望を集めます。統括管理の仕事に適しています。` },
    'ko':    { desc: (p) => `장성이 ${p}에 앉아 있습니다. 리더십 재능이 있고 권위감이 강하며 사람들 사이에서 자연스럽게 인망을 모읍니다. 통괄 관리 업무에 적합합니다。` },
  },
  '魁罡日': {
    'zh-CN': {
      desc: (_, __, extra) => `魁罡日柱（${extra}），聪明果断，气势非凡，性格刚正，处事干脆利落。需注意刚柔并济，避免过于强势影响人际关系。`,
      resolve: () => '多修柔和之道，刚柔相济则贵气愈显',
    },
    'zh-HK': {
      desc: (_, __, extra) => `魁罡日柱（${extra}），聰明果斷，氣勢非凡，性格剛正，處事乾脆俐落。需注意剛柔並濟，避免過於強勢影響人際關係。`,
      resolve: () => '多修柔和之道，剛柔相濟則貴氣愈顯',
    },
    'en': {
      desc: (_, __, extra) => `Kui Gang day pillar (${extra}) — sharp-minded, decisive, and commanding. Principled and direct in all matters. Balance firmness with flexibility to avoid alienating others through excessive dominance.`,
      resolve: () => 'Cultivate gentleness alongside strength; when balanced, your noble energy shines even brighter.',
    },
    'ja': {
      desc: (_, __, extra) => `魁罡日柱（${extra}）、聡明で果断、気勢が非凡で性格が剛正、物事の処理が明快です。剛柔のバランスを保ち、強すぎる姿勢が人間関係に影響しないよう注意が必要です。`,
      resolve: () => '柔和の道を多く修め、剛柔相済えば貴気がより一層輝きます。',
    },
    'ko': {
      desc: (_, __, extra) => `괴강 일주(${extra}), 총명하고 과단하며 기세가 비범하고 성격이 강직하며 일 처리가 명쾌합니다. 강유의 균형을 유지하고 지나치게 강한 태도가 인간관계에 영향을 미치지 않도록 주의해야 합니다。`,
      resolve: () => '유화의 도를 많이 닦으면 강유가 조화를 이루어 귀기가 더욱 빛납니다。',
    },
  },
  '孤辰': {
    'zh-CN': {
      desc: (p) => `孤辰坐${p}，情感细腻，独立性强，感情路上有时感到孤独。`,
      resolve: () => '命理认识自我，感情需主动经营，多参与社交活动，敞开心扉接受缘分',
    },
    'zh-HK': {
      desc: (p) => `孤辰坐${p}，情感細膩，獨立性強，感情路上有時感到孤獨。`,
      resolve: () => '命理認識自我，感情需主動經營，多參與社交活動，敞開心扉接受緣分',
    },
    'en': {
      desc: (p) => `Lone Star in the ${p} pillar — emotionally sensitive and highly independent, sometimes feeling lonely on the path of love.`,
      resolve: () => 'Use astrology for self-understanding. Actively nurture relationships, participate in social activities, and open your heart to new connections.',
    },
    'ja': {
      desc: (p) => `孤辰が${p}に坐します。感情が繊細で独立心が強く、恋愛の道で孤独を感じることがあります。`,
      resolve: () => '命理で自己を理解し、感情は積極的に育む必要があります。社交活動に多く参加し、心を開いて縁を受け入れましょう。',
    },
    'ko': {
      desc: (p) => `고진이 ${p}에 앉아 있습니다. 감정이 섬세하고 독립심이 강하며 사랑의 길에서 때로 외로움을 느낍니다。`,
      resolve: () => '명리로 자신을 이해하고 감정은 적극적으로 가꾸어야 합니다. 사교 활동에 많이 참여하고 마음을 열어 인연을 받아들이세요。',
    },
  },
  '寡宿': {
    'zh-CN': {
      desc: (p) => `寡宿坐${p}，感情世界较为内敛，有时显得与外界疏离。`,
      resolve: () => '这是性格独立的体现，建议多参与社群活动，主动建立亲密连接，婚姻宜晚不宜早',
    },
    'zh-HK': {
      desc: (p) => `寡宿坐${p}，感情世界較為內斂，有時顯得與外界疏離。`,
      resolve: () => '這是性格獨立的體現，建議多參與社群活動，主動建立親密連接，婚姻宜晚不宜早',
    },
    'en': {
      desc: (p) => `Widow Star in the ${p} pillar — emotionally reserved, sometimes appearing distant from the outside world.`,
      resolve: () => 'This reflects an independent nature. Participate more in community activities, proactively build close connections, and consider marrying later rather than earlier.',
    },
    'ja': {
      desc: (p) => `寡宿が${p}に坐します。感情世界が内向きで、時に外界と距離を置いているように見えます。`,
      resolve: () => 'これは独立した性格の表れです。コミュニティ活動に多く参加し、積極的に親密な繋がりを築き、結婚は早婚より晩婚が望ましいです。',
    },
    'ko': {
      desc: (p) => `과숙이 ${p}에 앉아 있습니다. 감정 세계가 내향적이며 때로 외부와 거리를 두는 것처럼 보입니다。`,
      resolve: () => '이는 독립적인 성격의 표현입니다. 커뮤니티 활동에 많이 참여하고 적극적으로 친밀한 관계를 맺으며 결혼은 조혼보다 만혼이 좋습니다。',
    },
  },
  '学堂': {
    'zh-CN': { desc: (p) => `学堂坐${p}，学业出众，聪明好学，文思敏捷，利于考试升学与学术研究。` },
    'zh-HK': { desc: (p) => `學堂坐${p}，學業出眾，聰明好學，文思敏捷，利於考試升學與學術研究。` },
    'en': { desc: (p) => `School Star in the ${p} pillar — outstanding academic ability, intelligent and studious. Favorable for exams, higher education, and scholarly pursuits.` },
    'ja': { desc: (p) => `学堂が${p}に坐します。学業が優れ、聡明で学習を好み、文才が鋭く、試験・進学・学術研究に有利です。` },
    'ko': { desc: (p) => `학당이 ${p}에 앉아 있습니다. 학업이 뛰어나고 총명하며 학습을 좋아하고 문재가 민첩해 시험·진학·학술 연구에 유리합니다。` },
  },
  '红艳煞': {
    'zh-CN': { desc: (p) => `红艳坐${p}，姿容出众，异性缘旺，魅力非凡。需防感情纠葛，以理智驾驭感情。` },
    'zh-HK': { desc: (p) => `紅艷坐${p}，姿容出眾，異性緣旺，魅力非凡。需防感情糾葛，以理智駕馭感情。` },
    'en': { desc: (p) => `Red Beauty in the ${p} pillar — striking appearance and magnetic charm. Guard against emotional entanglements; let reason guide your relationships.` },
    'ja': { desc: (p) => `紅艶が${p}に坐します。容姿が優れ、異性縁が旺盛で魅力が非凡です。感情のもつれに注意し、理性で感情を制御しましょう。` },
    'ko': { desc: (p) => `홍염이 ${p}에 앉아 있습니다. 자모가 뛰어나고 이성 인연이 왕성하며 매력이 비범합니다. 감정의 얽힘을 방지하고 이성으로 감정을 다스리세요。` },
  },
  '灾煞': {
    'zh-CN': {
      desc: (p) => `灾煞坐${p}，提示近期宜多关注健康与日常安全，理财方面保持谨慎。此为提醒调整节奏之象，非定论。`,
      resolve: () => '宜保持规律作息，出行前做好安全检查，避免高风险活动，关注身体信号并及时调养',
    },
    'zh-HK': {
      desc: (p) => `災煞坐${p}，提示近期宜多關注健康與日常安全，理財方面保持謹慎。此為提醒調整節奏之象，非定論。`,
      resolve: () => '宜保持規律作息，出行前做好安全檢查，避免高風險活動，關注身體信號並及時調養',
    },
    'en': {
      desc: (p) => `Calamity Star in the ${p} pillar — a gentle reminder to pay extra attention to health and daily safety. Keep finances prudent during this period. This is a signal to adjust your rhythm, not a fixed outcome.`,
      resolve: () => 'Maintain a regular routine, check safety before traveling, avoid high-risk activities, and listen to your body\'s signals for timely care.',
    },
    'ja': {
      desc: (p) => `災煞が${p}に坐します。この時期は健康と日常の安全に少し意識を向け、財務面でも慎重に進めると安心です。これはリズム調整のサインであり、固定された結果ではありません。`,
      resolve: () => '規則正しい生活を保ち、出行前に安全確認をし、高リスク活動を避け、体のサインに耳を傾けて適切にケアしてください。',
    },
    'ko': {
      desc: (p) => `재살이 ${p}에 앉아 있습니다. 이 시기에는 건강과 일상 안전에 조금 더 신경 쓰고 재무 면에서도 신중하게 대처하면 좋습니다. 이는 리듬 조정의 신호이며 고정된 결과가 아닙니다。`,
      resolve: () => '규칙적인 생활을 유지하고 출행 전 안전을 확인하며 고위험 활동을 피하고 몸의 신호에 귀 기울여 적절히 관리하세요。',
    },
  },
  '红鸾': {
    'zh-CN': { desc: (p) => `红鸾坐${p}，主婚姻喜事，感情顺遂，易得良缘，适合谈婚论嫁。` },
    'zh-HK': { desc: (p) => `紅鸞坐${p}，主婚姻喜事，感情順遂，易得良緣，適合談婚論嫁。` },
    'en': { desc: (p) => `Red Phoenix in the ${p} pillar — brings marriage blessings. Relationships proceed smoothly, and a good match is easily found. Favorable for courtship and marriage.` },
    'ja': { desc: (p) => `紅鸞が${p}に坐します。結婚の喜事を主り、感情が順調に進み、良縁を得やすく、縁談や結婚に適しています。` },
    'ko': { desc: (p) => `홍란이 ${p}에 앉아 있습니다. 혼인의 경사를 주관하며 감정이 순조롭게 진행하고 양연을 얻기 쉬워 혼담과 결혼에 적합합니다。` },
  },
  '天喜': {
    'zh-CN': { desc: (p) => `天喜坐${p}，主喜庆之事，心情愉悦，易得意外之喜，适合社交聚会。` },
    'zh-HK': { desc: (p) => `天喜坐${p}，主喜慶之事，心情愉悅，易得意外之喜，適合社交聚會。` },
    'en': { desc: (p) => `Heavenly Joy in the ${p} pillar — brings celebrations and happiness. Mood is uplifted, and pleasant surprises arrive easily. Favorable for social gatherings.` },
    'ja': { desc: (p) => `天喜が${p}に坐します。慶事を主り、気分が良く、思わぬ喜びを得やすく、社交・集まりに適しています。` },
    'ko': { desc: (p) => `천희가 ${p}에 앉아 있습니다. 경사를 주관하며 기분이 유쾌하고 뜻밖의 기쁨을 얻기 쉬워 사교 모임에 적합합니다。` },
  },
  '天医': {
    'zh-CN': { desc: (p) => `天医坐${p}，主健康平安，身体康复力强，适合从事医疗、保健、养生相关事业。` },
    'zh-HK': { desc: (p) => `天醫坐${p}，主健康平安，身體康復力強，適合從事醫療、保健、養生相關事業。` },
    'en': { desc: (p) => `Heavenly Doctor in the ${p} pillar — signifies health and well-being. Strong recovery ability. Suited for medicine, healthcare, and wellness-related careers.` },
    'ja': { desc: (p) => `天医が${p}に坐します。健康と平安を主り、身体の回復力が強く、医療・保健・養生関連の事業に適しています。` },
    'ko': { desc: (p) => `천의가 ${p}에 앉아 있습니다. 건강과 평안을 주관하며 신체 회복력이 강해 의료·보건·양생 관련 사업에 적합합니다。` },
  },
  '咸池': {
    'zh-CN': { desc: (p) => `咸池坐${p}，感情丰富，异性缘旺盛，但易生桃花劫，需防感情纠葛与情感纠纷。`, resolve: () => '感情中保持理智，避免暧昧不清的关系，以真心对待每一段缘分' },
    'zh-HK': { desc: (p) => `咸池坐${p}，感情豐富，異性緣旺盛，但易生桃花劫，需防感情糾葛與情感糾紛。`, resolve: () => '感情中保持理智，避免曖昧不清的關係，以真心對待每一段緣分' },
    'en': { desc: (p) => `Xian Chi in the ${p} pillar — rich in emotions with abundant romantic appeal, but prone to romantic entanglements. Guard against emotional disputes.`, resolve: () => 'Stay rational in relationships, avoid ambiguous connections, and treat every bond with sincerity.' },
    'ja': { desc: (p) => `咸池が${p}に坐します。感情が豊かで異性縁が旺盛ですが、桃花劫を生じやすく、感情のもつれとトラブルを防ぐ必要があります。`, resolve: () => '感情の中で理性を保ち、曖昧な関係を避け、すべての縁を真心で大切にしましょう。' },
    'ko': { desc: (p) => `함지가 ${p}에 앉아 있습니다. 감정이 풍부하고 이성 인연이 왕성하지만 도화재를 일으키기 쉬우며 감정의 얽힘과 분쟁을 방지해야 합니다。`, resolve: () => '감정 속에서 이성을 유지하고 애매한 관계를 피하며 모든 인연을 진심으로 대하세요。' },
  },
  '大耗': {
    'zh-CN': { desc: (p) => `大耗坐${p}，提示近期宜注意收支平衡，合理规划财务，避免冲动消费。此为理财提醒之象，非定论。`, resolve: () => '宜稳健理财，量入为出，避免大额投资与借贷，做好长期财务规划' },
    'zh-HK': { desc: (p) => `大耗坐${p}，提示近期宜注意收支平衡，合理規劃財務，避免衝動消費。此為理財提醒之象，非定論。`, resolve: () => '宜穩健理財，量入為出，避免大額投資與借貸，做好長期財務規劃' },
    'en': { desc: (p) => `Great Loss in the ${p} pillar — a gentle reminder to watch your income-expense balance and plan finances thoughtfully. Avoid impulse spending. This is a financial awareness signal, not a fixed outcome.`, resolve: () => 'Practice prudent financial management, spend within your means, avoid large investments and loans, and build a solid long-term financial plan.' },
    'ja': { desc: (p) => `大耗が${p}に坐します。この時期は収支バランスに注意し、衝動的な支出を控えて計画的な財務管理を心がけましょう。これは財務意識を高めるサインであり、固定された結果ではありません。`, resolve: () => '堅実な理財を心がけ、収入に応じて支出し、大きな投資と借入を避け、長期的な財務計画をしっかり立ててください。' },
    'ko': { desc: (p) => `대모가 ${p}에 앉아 있습니다. 이 시기에는 수입과 지출의 균형에 주의하고 충동적인 소비를 자제하며 계획적인 재무 관리를 하면 좋습니다。이는 재정 인식을 높이는 신호이며 고정된 결과가 아닙니다。`, resolve: () => '건전한 재무 관리를 하고 수입에 맞춰 지출하며 대액 투자와 대출을 피하고 장기적인 재무 계획을 철저히 세우세요。' },
  },
  '血刃': {
    'zh-CN': { desc: (p) => `血刃坐${p}，提示近期宜多加留意身体安全，日常出行和运动中保持适度谨慎。此为关注健康的温和提醒，非定凶之象。`, resolve: () => '避免参与过于激烈的对抗性运动，出行时注意交通规则，养成定期体检关注健康的好习惯' },
    'zh-HK': { desc: (p) => `血刃坐${p}，提示近期宜多加留意身體安全，日常出行和運動中保持適度謹慎。此為關注健康的溫和提醒，非定凶之象。`, resolve: () => '避免參與過於激烈的對抗性運動，出行時注意交通規則，養成定期體檢關注健康的好習慣' },
    'en': { desc: (p) => `Blood Blade in the ${p} pillar — a gentle nudge to pay extra attention to physical safety. Exercise moderation in daily travel and activities. This is a mild health reminder, not a fixed outcome.`, resolve: () => 'Avoid overly intense contact sports, follow traffic rules when traveling, and build the habit of regular health check-ups.' },
    'ja': { desc: (p) => `血刃が${p}に坐します。この時期は身体の安全に少し気を配り、日常の移動や運動では適度な注意を心がけましょう。これは健康へのやさしい注意喚起であり、固定された凶兆ではありません。`, resolve: () => '激しいコンタクトスポーツを避け、出行時は交通ルールを守り、定期的な健康診断を習慣にしてください。' },
    'ko': { desc: (p) => `혈인이 ${p}에 앉아 있습니다. 이 시기에는 신체 안전에 조금 더 신경 쓰고 일상 이동과 운동에서 적절한 주의를 기울이면 좋습니다. 이는 건강을 위한 부드러운 알림이며 고정된 흉조가 아닙니다。`, resolve: () => '격한 접촉 스포츠를 피하고 출행 시 교통 규칙을 지키며 정기 건강검진을 습관화하세요。' },
  },
  '吊客': {
    'zh-CN': { desc: (p) => `吊客坐${p}，提示近期宜多关心家人与亲友，珍惜相聚时光。此为提醒珍惜身边人的温和信号，非定论。`, resolve: () => '珍惜身边人，多陪伴家人朋友，保持积极心态面对生活的起伏，善意与温情是最好的化解' },
    'zh-HK': { desc: (p) => `吊客坐${p}，提示近期宜多關心家人與親友，珍惜相聚時光。此為提醒珍惜身邊人的溫和信號，非定論。`, resolve: () => '珍惜身邊人，多陪伴家人朋友，保持積極心態面對生活的起伏，善意與溫情是最好的化解' },
    'en': { desc: (p) => `Mourning Guest in the ${p} pillar — a gentle reminder to cherish time with family and loved ones. This is a mild signal to treasure those around you, not a fixed outcome.`, resolve: () => 'Cherish those around you, spend quality time with family and friends, maintain a positive outlook through the ups and downs of life, and let kindness and warmth be your guiding light.' },
    'ja': { desc: (p) => `吊客が${p}に坐します。この時期は家族や親しい人との時間を大切にし、共に過ごす瞬間を慈しむと良いでしょう。これは身近な人を大切にするやさしいサインであり、固定された結果ではありません。`, resolve: () => '身近な人を大切にし、家族や友人との時間を楽しみ、人生の変化に前向きな心で向き合いましょう。善意と温かさが最良の解決策です。' },
    'ko': { desc: (p) => `조객이 ${p}에 앉아 있습니다. 이 시기에는 가족과 친지와의 시간을 소중히 여기고 함께하는 순간을 즐기면 좋습니다。이는 곁의 사람들을 소중히 하라는 부드러운 신호이며 고정된 결과가 아닙니다。`, resolve: () => '곁에 있는 사람을 소중히 하고 가족과 친구와 많은 시간을 보내며 인생의 변화에 긍정적인 마음으로 임하세요。선의와 따뜻함이 가장 좋은 해결책입니다。' },
  },
  '丧门': {
    'zh-CN': { desc: (p) => `丧门坐${p}，提示近期宜关注家人健康与家庭氛围，多些关怀与陪伴。此为提醒珍视家庭的温和信号，非定论。`, resolve: () => '多关心家人健康，营造温暖的家庭氛围，保持乐观心态面对生活中的变化与转折' },
    'zh-HK': { desc: (p) => `喪門坐${p}，提示近期宜關注家人健康與家庭氛圍，多些關懷與陪伴。此為提醒珍視家庭的溫和信號，非定論。`, resolve: () => '多關心家人健康，營造溫暖的家庭氛圍，保持樂觀心態面對生活中的變化與轉折' },
    'en': { desc: (p) => `Funeral Gate in the ${p} pillar — a gentle reminder to pay attention to family health and home atmosphere, with extra care and companionship. This is a mild signal to cherish family, not a fixed outcome.`, resolve: () => 'Care for family health, create a warm home atmosphere, and stay optimistic when facing the changes and transitions of life.' },
    'ja': { desc: (p) => `喪門が${p}に坐します。この時期は家族の健康と家庭の雰囲気に気を配り、思いやりと寄り添いを大切にすると良いでしょう。これは家族を大切にするやさしいサインであり、固定された結果ではありません。`, resolve: () => '家族の健康に関心を持ち、温かな家庭の雰囲気を育み、人生の変化や転機に前向きな心で向き合いましょう。' },
    'ko': { desc: (p) => `상문이 ${p}에 앉아 있습니다. 이 시기에는 가족의 건강과 가정 분위기에 관심을 기울이고 배려와 함께함을 소중히 하면 좋습니다。이는 가족을 소중히 하라는 부드러운 신호이며 고정된 결과가 아닙니다。`, resolve: () => '가족의 건강을 챙기고 따뜻한 가정 분위기를 만들며 인생의 변화와 전환점에 낙관적인 마음으로 임하세요。' },
  },
  '天德合': {
    'zh-CN': { desc: (p) => `天德合坐${p}，为天德之合气，主逢凶化吉、处事得人情助力，宜修德守正。` },
    'zh-HK': { desc: (p) => `天德合坐${p}，為天德之合氣，主逢凶化吉、處事得人情助力，宜修德守正。` },
    'en': { desc: (p) => `Heavenly Virtue Harmony in the ${p} pillar — a harmonized virtue signal that softens adversity and favors ethical action.` },
    'ja': { desc: (p) => `天徳合が${p}に坐し、徳の合気として困難を和らげ、正道を守るほど助力を得やすい配置です。` },
    'ko': { desc: (p) => `천덕합이 ${p}에 있어 덕의 합기로 어려움을 부드럽게 하고 바른 태도에 도움이 따릅니다。` },
  },
  '月德合': {
    'zh-CN': { desc: (p) => `月德合坐${p}，承月德合化之气，主心性温和、遇事易得缓冲与贵助。` },
    'zh-HK': { desc: (p) => `月德合坐${p}，承月德合化之氣，主心性溫和、遇事易得緩衝與貴助。` },
    'en': { desc: (p) => `Monthly Virtue Harmony in the ${p} pillar — softens pressure and supports help through gentle conduct.` },
    'ja': { desc: (p) => `月徳合が${p}に坐し、圧力を和らげ、穏やかな振る舞いによって助力を得やすい配置です。` },
    'ko': { desc: (p) => `월덕합이 ${p}에 있어 압력을 완화하고 온화한 처신으로 도움을 얻기 쉽습니다。` },
  },
  '十恶大败': {
    'zh-CN': { desc: () => '十恶大败日生，禄入空亡，财帛易散，聚财须谨慎。' },
    'zh-HK': { desc: (_, __, extra) => `日柱為十惡大敗（${extra || ''}），古法重視財祿起伏，現代解讀宜重視信用、現金流與長期規劃。` },
    'en': { desc: (_, __, extra) => `Ten Defeats Day (${extra || ''}) — traditionally linked to resource volatility; modern reading emphasizes credit, cash flow, and planning.` },
  },
  '八专日': {
    'zh-CN': { desc: () => '八专日生，干支同气，情执而专，夫妻缘须多经营。' },
    'zh-HK': { desc: (_, __, extra) => `日柱為八專日（${extra || ''}），干支氣勢專一，個性與取向較鮮明，宜以專長立身。` },
    'en': { desc: (_, __, extra) => `Eight Exclusive Day (${extra || ''}) — concentrated pillar energy, favoring clear strengths and specialist development.` },
  },
  '六秀日': {
    'zh-CN': { desc: () => '六秀日生，聪秀文雅，才华出众，人缘清贵。' },
    'zh-HK': { desc: (_, __, extra) => `日柱為六秀日（${extra || ''}），秀氣入命，利才藝、表達、審美與個人魅力。` },
    'en': { desc: (_, __, extra) => `Six Elegance Day (${extra || ''}) — supports talent, expression, aesthetics, and personal charm.` },
  },
  '童子煞': {
    'zh-CN': { desc: (p) => `童子煞坐${p}，古法主灵敏清高、心性敏感，现代解读宜重视情绪安顿与稳定节奏。` },
    'zh-HK': { desc: (p) => `童子煞坐${p}，古法主靈敏清高、心性敏感，現代解讀宜重視情緒安頓與穩定節奏。` },
    'en': { desc: (p) => `Child Star Sha in the ${p} pillar — sensitivity and refined temperament; keep emotions grounded and routines steady.` },
  },
  '流霞': {
    'zh-CN': { desc: (p) => `流霞坐${p}，主气色情感流动，魅力较显，也需留意情绪与关系边界。` },
    'zh-HK': { desc: (p) => `流霞坐${p}，主氣色情感流動，魅力較顯，也需留意情緒與關係邊界。` },
    'en': { desc: (p) => `Flowing Rosy Cloud in the ${p} pillar — expressive charm and emotional flow; keep boundaries clear.` },
  },
  '披麻': {
    'zh-CN': { desc: (p) => `披麻坐${p}，古法多作家宅亲缘提醒，现代解读宜多关照家人健康与情绪。` },
    'zh-HK': { desc: (p) => `披麻坐${p}，古法多作家宅親緣提醒，現代解讀宜多關照家人健康與情緒。` },
    'en': { desc: (p) => `Mourning Cloth in the ${p} pillar — a family-care reminder; pay attention to relatives' wellbeing and emotions.` },
  },
  '空亡': {
    'zh-CN': { desc: (p) => `空亡落${p}，旬空气虚，相关柱象宜看作参考信号，重大事项需更重落实与验证。` },
    'zh-HK': { desc: (p) => `空亡落${p}，旬空氣虛，相關柱象宜看作參考信號，重大事項需更重落實與驗證。` },
    'en': { desc: (p) => `Void in the ${p} pillar — the pillar's signal can be less grounded; verify details before major action.` },
  },
'三奇贵人': {
    'zh-CN': { desc: () => `三奇贵人入命，襟怀卓越，才华奇特，易得际遇垂青，成就非凡。` },
    'zh-HK': { desc: () => `三奇貴人入命，襟懷卓越，才華奇特，易得際遇垂青，成就非凡。` },
    'en':    { desc: () => `Three Wonders enters the chart — a visionary spirit and remarkable talent. Fortune favors you with rare opportunities and extraordinary achievement.` },
    'ja':    { desc: () => `三奇貴人が命に入ります。襟懐が卓越し才華が奇抜で、めったにない機会に恵まれ、非凡な成就を遂げます。` },
    'ko':    { desc: () => `삼기귀인이 명에 들어와 품회가 탁월하고 재능이 기이하여 좋은 기회를 만나기 쉬우며 비범한 성취를 이룹니다。` },
  },
  '天罗': {
    'zh-CN': { desc: (p) => `天罗临命，火命遇戌亥，运势易受牵绊，宜静不宜躁。`, resolve: () => '遇事勿急进，留三分余地；规律作息，复杂事务先理清边界再推进' },
    'zh-HK': { desc: (p) => `天羅臨命，火命遇戌亥，運勢易受牽絆，宜靜不宜躁。`, resolve: () => '遇事勿急進，留三分餘地；規律作息，複雜事務先理清邊界再推進' },
    'en':    { desc: (p) => `Sky Net looms over the chart — fire-natured charts meeting Xu/Hai are easily hindered. Favor calm over haste.`, resolve: () => 'Do not rush into matters; keep room for maneuvering. Maintain a regular routine and clarify boundaries before advancing complex affairs.' },
    'ja':    { desc: (p) => `天羅が命に臨みます。火命が戌亥に遇うと運勢は妨げられやすく、穏やかに行動し焦らないことが大切です。`, resolve: () => '物事に急ぎすぎず、余裕を残しましょう。規則正しい生活を心がけ、複雑な事は範囲を明確にしてから進めてください。' },
    'ko':    { desc: (p) => `천라가 명에 임합니다. 화명이 술해를 만나면 운세가 방해받기 쉬우니 조용히 행동하고 조급해하지 마세요。`, resolve: () => '일에 성급하게 나가지 말고 여유를 남기세요. 규칙적인 생활을 챙기고 복잡한 일은 범위를 명확히 한 후 진행하세요。' },
  },
  '地网': {
    'zh-CN': { desc: (p) => `地网临命，水土命遇辰巳，谋事多阻，宜稳扎稳打。`, resolve: () => '遇事勿急进，留三分余地；规律作息，复杂事务先理清边界再推进' },
    'zh-HK': { desc: (p) => `地網臨命，水土命遇辰巳，謀事多阻，宜穩紮穩打。`, resolve: () => '遇事勿急進，留三分餘地；規律作息，複雜事務先理清邊界再推進' },
    'en':    { desc: (p) => `Earth Net looms over the chart — earth/water-natured charts meeting Chen/Si face many obstacles. Favor steady, step-by-step progress.`, resolve: () => 'Do not rush into matters; keep room for maneuvering. Maintain a regular routine and clarify boundaries before advancing complex affairs.' },
    'ja':    { desc: (p) => `地網が命に臨みます。水土命が辰巳に遇うと計画は阻まれやすく、着実に一歩ずつ進むことが大切です。`, resolve: () => '物事に急ぎすぎず、余裕を残しましょう。規則正しい生活を心がけ、複雑な事は範囲を明確にしてから進めてください。' },
    'ko':    { desc: (p) => `지망이 명에 임합니다. 수토명이 진사를 만나면 일이 많이 막히니 차근차근 안정적으로 나아가세요。`, resolve: () => '일에 성급하게 나가지 말고 여유를 남기세요. 규칙적인 생활을 챙기고 복잡한 일은 범위를 명확히 한 후 진행하세요。' },
  },
  '阴差阳错': {
    'zh-CN': { desc: (p) => `阴差阳错日生，人际关系易生误会，姻缘多波折。`, resolve: () => '感情沟通直白坦率，重大决定避开情绪波动期；文书契约细读条款' },
    'zh-HK': { desc: (p) => `陰差陽錯日生，人際關係易生誤會，姻緣多波折。`, resolve: () => '感情溝通直白坦率，重大決定避開情緒波動期；文書契約細讀條款' },
    'en':    { desc: (p) => `Born on a Yin-Yang Error day — prone to misunderstandings in relationships and a rocky path in love.`, resolve: () => 'Communicate directly and honestly in relationships. Avoid making major decisions during emotional turbulence, and read contract terms carefully.' },
    'ja':    { desc: (p) => `陰差陽錯の日生まれです。人間関係で誤解が生じやすく、姻縁には波乱が多くあります。`, resolve: () => '感情は率直に伝え合いましょう。大きな決断は感情の波が大きい時期を避け、契約書は条項をよく読みましょう。' },
    'ko':    { desc: (p) => `음차양착일에 태어났습니다. 인간관계에서 오해가 생기기 쉽고 인연에 파란이 많습니다。`, resolve: () => '감정은 직설적이고 솔직하게 소통하세요. 중대한 결정은 감정이 흔들리는 시기를 피하고 계약서는 조항을 꼼꼼히 읽으세요。' },
  },
  '天赦日': {
    'zh-CN': { desc: (p) => `天赦日生，百事可解，逢凶化吉，一生多贵人庇佑。` },
    'zh-HK': { desc: (p) => `天赦日生，百事可解，逢凶化吉，一生多貴人庇佑。` },
    'en':    { desc: (p) => `Born on a Heaven's Pardon day — every difficulty can be resolved. Misfortune turns to fortune, blessed by noble helpers throughout life.` },
    'ja':    { desc: (p) => `天赦日の日生まれです。何事も解決でき、凶が吉に転じ、一生を通じて貴人の加護を受けます。` },
    'ko':    { desc: (p) => `천사일에 태어났습니다. 모든 일이 풀릴 수 있고 흉이 길로 바뀌며 평생 귀인의 보호를 받습니다。` },
  },
  '四废日': {
    'zh-CN': { desc: (p) => `四废日生，做事易半途而废，体力意志须多加锤炼。`, resolve: () => '目标拆小步推进，借团队与习惯之力坚持，运动强身' },
    'zh-HK': { desc: (p) => `四廢日生，做事易半途而廢，體力意志須多加錘煉。`, resolve: () => '目標拆小步推進，借團隊與習慣之力堅持，運動強身' },
    'en':    { desc: (p) => `Born on a Four Waste day — prone to leaving things unfinished. Strengthen physical stamina and willpower deliberately.`, resolve: () => 'Break goals into small steps and advance steadily. Rely on teamwork and habits to persevere, and exercise to build strength.' },
    'ja':    { desc: (p) => `四廃日の日生まれです。物事が途中で止まりやすく、体力と意志力を鍛える必要があります。`, resolve: () => '目標を小さく分けて進め、チームや習慣の力で粘り強く取り組み、運動で身体を鍛えましょう。' },
    'ko':    { desc: (p) => `사폐일에 태어났습니다. 일이 중도에 그치기 쉬우니 체력과 의지력을 단련해야 합니다。`, resolve: () => '목표를 작은 단계로 나눠 진행하고 팀과 습관의 힘으로 꾸준히 하며 운동으로 몸을 단련하세요。' },
  },
  '勾绞煞': {
    'zh-CN': { desc: (p) => `勾绞煞临命，易惹口舌纠纷，人际间须防牵连缠绕。`, resolve: () => '少涉是非之地，重要合作权责书面化' },
    'zh-HK': { desc: (p) => `勾絞煞臨命，易惹口舌糾紛，人際間須防牽連纏繞。`, resolve: () => '少涉是非之地，重要合作權責書面化' },
    'en':    { desc: (p) => `Entangle Star looms over the chart — prone to disputes and gossip. Guard against being dragged into others' quarrels.`, resolve: () => 'Stay away from places of gossip and conflict, and formalize rights and responsibilities in important collaborations in writing.' },
    'ja':    { desc: (p) => `勾絞煞が命に臨みます。口舌のトラブルが起きやすく、人間関係の縺れを防ぐ必要があります。`, resolve: () => 'トラブルの多い場所には関わらず、重要な協力は権限と責任を書面化しましょう。' },
    'ko':    { desc: (p) => `구교살이 명에 임합니다. 구설 분쟁이 생기기 쉬우니 인간관계에서 얽히는 것을 방지해야 합니다。`, resolve: () => '시비의 장소에는 덜 관여하고 중요한 협력은 권한과 책임을 문서화하세요。' },
  },
  '正学堂': {
    'zh-CN': { desc: (p) => `正学堂入命，学业聪颖，科名有望，利考试进修。` },
    'zh-HK': { desc: (p) => `正學堂入命，學業聰穎，科名有望，利考試進修。` },
    'en':    { desc: (p) => `True Academy enters the chart — brilliant in studies with promising exam results. Favorable for examinations and further education.` },
    'ja':    { desc: (p) => `正学堂が命に入ります。学業が優秀で試験に有望で、受験や研修に有利です。` },
    'ko':    { desc: (p) => `정학당이 명에 들어와 학업이 총명하고 시험에 유망하며 수험과 연수에 유리합니다。` },
  },
  '正词馆': {
    'zh-CN': { desc: (p) => `正词馆入命，文采出众，利文职、著作、教培之途。` },
    'zh-HK': { desc: (p) => `正詞館入命，文采出眾，利文職、著作、教培之途。` },
    'en':    { desc: (p) => `True Institute enters the chart — outstanding literary talent. Favorable for clerical, literary, and educational careers.` },
    'ja':    { desc: (p) => `正詞館が命に入ります。文才が優れ、文職・著述・教育の道に有利です。` },
    'ko':    { desc: (p) => `정사관이 명에 들어와 문재가 뛰어나며 문직·저술·교육의 길에 유리합니다。` },
  },
  '金神': {
    'zh-CN': { desc: (p) => `金神入命，性刚威猛，破坏力强；入火乡反而富贵可期。`, resolve: () => '刚极易折，习柔性沟通；将冲劲投入竞技、开拓类事务' },
    'zh-HK': { desc: (p) => `金神入命，性剛威猛，破壞力強；入火鄉反而富貴可期。`, resolve: () => '剛極易折，習柔性溝通；將衝勁投入競技、開拓類事務' },
    'en':    { desc: (p) => `Metal Spirit enters the chart — a forceful and formidable nature. In the Fire domain, wealth and honor can instead be expected.`, resolve: () => 'Rigidity easily breaks — learn flexible communication. Channel your drive into competitive and pioneering endeavors.' },
    'ja':    { desc: (p) => `金神が命に入ります。剛猛で威勢が強く破壊力がありますが、火郷に入ればむしろ富貴が期待できます。`, resolve: () => '剛直すぎると折れやすいので柔軟なコミュニケーションを学び、勢いを競技や開拓の分野に注ぎましょう。' },
    'ko':    { desc: (p) => `금신이 명에 들어와 성격이 강하고 위엄이 있으며 파괴력이 강합니다. 화향에 들어가면 오히려 부귀를 기대할 수 있습니다。`, resolve: () => '너무 강하면 부러지기 쉬우니 유연한 소통을 익히고 기세를 경기·개척 분야에 쏟으세요。' },
  },
  '拱禄': {
    'zh-CN': { desc: (p) => `拱禄入命，暗禄拱身，衣食无缺，福泽绵长。` },
    'zh-HK': { desc: (p) => `拱祿入命，暗祿拱身，衣食無缺，福澤綿長。` },
    'en':    { desc: (p) => `Embraced Prosperity enters the chart — hidden prosperity supports you. Food and clothing are never lacking, and blessings endure.` },
    'ja':    { desc: (p) => `拱禄が命に入ります。暗禄が身を支え、衣食に欠けることなく福沢が長く続きます。` },
    'ko':    { desc: (p) => `공록이 명에 들어와 암록이 몸을 받쳐주며 의식에 부족함이 없고 복택이 길게 이어집니다。` },
  },
  '天转日': {
    'zh-CN': { desc: (p) => `天转日生，气过旺而易折，盛极须防反转。`, resolve: () => '高光时保持收敛，避免冒进扩张' },
    'zh-HK': { desc: (p) => `天轉日生，氣過旺而易折，盛極須防反轉。`, resolve: () => '高光時保持收斂，避免冒進擴張' },
    'en':    { desc: (p) => `Born on a Sky Turn day — excessive energy breaks easily. Guard against reversal at the peak of prosperity.`, resolve: () => 'Stay restrained during moments of glory and avoid rash expansion.' },
    'ja':    { desc: (p) => `天転日の日生まれです。気が強すぎて折れやすく、全盛期には反転に注意が必要です。`, resolve: () => '輝いている時こそ控えめにし、冒険的な拡大を避けましょう。' },
    'ko':    { desc: (p) => `천전일에 태어났습니다. 기가 지나치게 왕성하여 꺾이기 쉬우니 성세가 극에 달할 때 반전을 조심하세요。`, resolve: () => '빛날 때일수록 절제하고 무모한 확장을 피하세요。' },
  },
  '地转日': {
    'zh-CN': { desc: (p) => `地转日生，运势多转折，起伏中须守正待时。`, resolve: () => '顺境储备，逆境守成，勿在低谷做重大决定' },
    'zh-HK': { desc: (p) => `地轉日生，運勢多轉折，起伏中須守正待時。`, resolve: () => '順境儲備，逆境守成，勿在低谷做重大決定' },
    'en':    { desc: (p) => `Born on an Earth Turn day — fortune takes many turns. Stay true to your principles and bide your time through the ups and downs.`, resolve: () => 'Save during favorable times and preserve during adversity. Never make major decisions in a low period.' },
    'ja':    { desc: (p) => `地転日の日生まれです。運勢に転換が多く、起伏の中で正道を守り時を待つことが大切です。`, resolve: () => '順境では備蓄し、逆境では守り、どん底で重大な決断をしないようにしましょう。' },
    'ko':    { desc: (p) => `지전일에 태어났습니다. 운세에 전환이 많으니 기복 속에서 정도를 지키며 때를 기다리세요。`, resolve: () => '순경에서는 비축하고 역경에서는 지키며 저점에서 중대한 결정을 하지 마세요。' },
  },
  '九丑日': {
    'zh-CN': { desc: (p) => `九丑日生，易招是非情扰，名誉须自珍。`, resolve: () => '谨言慎行，远离暧昧纠纷' },
    'zh-HK': { desc: (p) => `九丑日生，易招是非情擾，名譽須自珍。`, resolve: () => '謹言慎行，遠離曖昧糾紛' },
    'en':    { desc: (p) => `Born on a Nine Ugliness day — prone to gossip and romantic turmoil. Guard your reputation carefully.`, resolve: () => 'Speak and act with caution, and stay away from ambiguous entanglements.' },
    'ja':    { desc: (p) => `九丑日の日生まれです。噂や情のもつれを招きやすいので、名誉を大切に守りましょう。`, resolve: () => '言葉と行動に慎みを持ち、曖昧なトラブルを避けましょう。' },
    'ko':    { desc: (p) => `구축일에 태어났습니다. 시비와 감정의 혼란이 생기기 쉬우니 명예를 스스로 아끼세요。`, resolve: () => '언행을 조심하고 애매한 분쟁에서 멀어지세요。' },
  },
  '飞刃': {
    'zh-CN': { desc: (p) => `飞刃临命，主刑伤磕碰，行事宜稳，注意安全。`, resolve: () => '驾驶运动做好防护，规避高危活动，定期体检' },
    'zh-HK': { desc: (p) => `飛刃臨命，主刑傷磕碰，行事宜穩，注意安全。`, resolve: () => '駕駛運動做好防護，規避高危活動，定期體檢' },
    'en':    { desc: (p) => `Flying Blade looms over the chart — indicates injuries and accidents. Act steadily and mind your safety.`, resolve: () => 'Take protective measures when driving and exercising, avoid high-risk activities, and get regular health check-ups.' },
    'ja':    { desc: (p) => `飛刃が命に臨みます。傷や衝突を主るので、行動は慎重にし安全に注意しましょう。`, resolve: () => '運転やスポーツでは防護を徹底し、高リスクの活動を避け、定期健診を受けましょう。' },
    'ko':    { desc: (p) => `비인이 명에 임합니다. 상처와 충돌을 주관하니 행동을 안정적으로 하고 안전에 주의하세요。`, resolve: () => '운전과 운동 시 보호 장구를 착용하고 고위험 활동을 피하며 정기 건강검진을 받으세요。' },
  },
};

function getI18nEntry(key: string, lang: Lang): ShenshaI18nEntry {
  return SHENSHA_I18N[key]?.[lang] ?? SHENSHA_I18N[key]?.['zh-CN'] ?? { desc: () => '' };
}

function joinPillars(labels: string[], lang: Lang): string {
  const sep = lang === 'ko' ? '·' : lang === 'en' ? ' & ' : '、';
  return labels.join(sep);
}
/**
 * 计算命局神煞（多语言版，问真主流口径规则驱动）
 * 返回结果已按 pillar 聚合
 * @param fourPillars 四柱
 * @param lang 语言（zh-CN/zh-HK/en/ja/ko）
 * @param gender 性别：1 男 / 2 女（仅元辰区分，默认 1）
 */
export function calcShensha(fourPillars: FourPillars, lang: Lang = 'zh-CN', gender: 1 | 2 = 1): ShenshaItem[] {
  const { year, month, day, hour } = fourPillars;
  const ganzhi = [
    `${year.stem}${year.branch}`,
    `${month.stem}${month.branch}`,
    `${day.stem}${day.branch}`,
    `${hour.stem}${hour.branch}`,
  ];
  const hits = calcShenshaHits([ganzhi[0], ganzhi[1], ganzhi[2], ganzhi[3]], gender);

  const result: ShenshaItem[] = [];
  for (const hit of hits) {
    const key = hit.name;
    const pillarLabels = Array.from(new Set(
      hit.pillarIndexes.map((i) => (i < 0 ? getStemLabel(lang) : getPillarName(i, lang))),
    ));
    const pillarsJoined = joinPillars(pillarLabels, lang);
    const cnt = hit.pillarIndexes.length;
    const entry = getI18nEntry(key, lang);
    const needsExtra = key === '魁罡日' || key === '十恶大败' || key === '八专日' || key === '六秀日';
    const extra = needsExtra ? ganzhi[2] : undefined;
    const level: ShenshaLevel = SHENSHA_LEVEL[key] ?? '中';
    const resolveText = entry.resolve?.(pillarsJoined, cnt, extra);
    result.push({
      name: getShenshaName(key, lang),
      pillars: pillarLabels,
      level,
      desc: entry.desc(pillarsJoined, cnt, extra),
      resolve: resolveText || undefined,
      ...buildColors(level),
      influenceScore: calcInfluenceScore(pillarLabels, level),
    });
  }
  // 按等级降序（大吉→吉→中→注意→化解）稳定排序；同级保持 order 序
  result.sort((a, b) => LEVEL_WEIGHT[a.level] - LEVEL_WEIGHT[b.level]);
  return result;
}

/**
 * 按神煞名反查规则 key（服务端返回多语言名时使用）。
 */
function getShenshaNameKey(name: string, lang: Lang): string | undefined {
  for (const [key, labels] of Object.entries(SHENSHA_NAMES)) {
    if (labels['zh-CN'] === name || labels[lang] === name || key === name) return key;
  }
  return undefined;
}

/**
 * 按神煞名构建展示用 ShenshaItem（服务端返回名字数组时补全字段）。
 * 找不到规则时回退最小项（等级「中」，蓝色系）。
 * @param positionLabel 无柱位信息时的兜底位置标签（服务端大运/流年神煞不含柱位）
 */
export function buildShenshaItemByName(name: string, lang: Lang = 'zh-CN', positionLabel = '命局'): ShenshaItem {
  const key = getShenshaNameKey(name, lang) ?? name;
  const entry = getI18nEntry(key, lang);
  const level: ShenshaLevel = SHENSHA_LEVEL[key] ?? '中';
  const pillarLabels: string[] = [];
  const pillarsJoined = joinPillars(pillarLabels, lang) || positionLabel;
  const desc = entry.desc(pillarsJoined, 1);
  const resolveText = entry.resolve?.(pillarsJoined, 1);
  // 服务端神煞名无柱位信息：以「时柱」权重(15)作为单柱基准，叠加等级加成，保证影响力非 0 且各等级有区分度
  const influenceScore = calcInfluenceScore(pillarLabels.length ? pillarLabels : ['时柱'], level);
  return {
    name,
    pillars: pillarLabels,
    level,
    desc,
    resolve: resolveText || undefined,
    ...buildColors(level),
    influenceScore,
  };
}

/**
 * 获取神煞总结文字（多语言版，带柱位影响力权重分析）
 */
export function getShenshaSummary(shensha: ShenshaItem[], lang: Lang = 'zh-CN'): string {
  const jiItems = shensha.filter(s => s.level === '吉' || s.level === '大吉');
  const zhongItems = shensha.filter(s => s.level === '中');
  const zhuyiItems = shensha.filter(s => s.level === '注意' || s.level === '化解');
  const jiCount = jiItems.length;
  const zhongCount = zhongItems.length;
  const zhuyiCount = zhuyiItems.length;

  // 按影响力排序取前3
  const topJi = [...jiItems].sort((a, b) => b.influenceScore - a.influenceScore).slice(0, 3);
  const topZhuyi = [...zhuyiItems].sort((a, b) => b.influenceScore - a.influenceScore).slice(0, 3);

  if (lang === 'en') {
    if (jiCount === 0 && zhuyiCount === 0) return 'Few special stars in your chart — steady and balanced fortune.';
    const parts: string[] = [];
    if (jiCount > 0) parts.push(`${jiCount} auspicious star${jiCount > 1 ? 's' : ''} blessing your chart`);
    if (zhongCount > 0) parts.push(`${zhongCount} neutral star${zhongCount > 1 ? 's' : ''} providing unique energy`);
    if (zhuyiCount > 0) parts.push(`${zhuyiCount} star${zhuyiCount > 1 ? 's' : ''} requiring attention`);
    let text = parts.join(', ') + '. Use astrology as a mirror for self-understanding — harness auspicious energy and wisely navigate challenges.';
    if (topJi.length > 0) text += ` Most influential: ${topJi.map(s => s.name).join(', ')}.`;
    if (topZhuyi.length > 0) text += ` Key cautions: ${topZhuyi.map(s => s.name).join(', ')}.`;
    return text;
  }
  if (lang === 'ja') {
    if (jiCount === 0 && zhuyiCount === 0) return '命局の神煞は少なく、運勢は安定しています。自然の流れに従い、着実に歩みましょう。';
    const parts: string[] = [];
    if (jiCount > 0) parts.push(`命局に${jiCount}つの吉星の加護があります`);
    if (zhongCount > 0) parts.push(`${zhongCount}つの中性神煞が独自のエネルギーを提供します`);
    if (zhuyiCount > 0) parts.push(`${zhuyiCount}つの注意が必要な神煞があります`);
    let text = parts.join('、') + '。命理は自己を知る鏡です。吉星の力を活かし、知恵で不利な要素を化解することで、吉を招き凶を避けられます。';
    if (topJi.length > 0) text += ` 影響力が最も高い吉星：${topJi.map(s => s.name).join('、')}。`;
    if (topZhuyi.length > 0) text += ` 特に注意すべき神煞：${topZhuyi.map(s => s.name).join('、')}。`;
    return text;
  }
  if (lang === 'ko') {
    if (jiCount === 0 && zhuyiCount === 0) return '명국 신살이 적어 운세가 안정적입니다. 자연의 흐름에 따라 착실하게 나아가세요.';
    const parts: string[] = [];
    if (jiCount > 0) parts.push(`명국에 ${jiCount}개의 길성이 있습니다`);
    if (zhongCount > 0) parts.push(`${zhongCount}개의 중성 신살이 독특한 에너지를 제공합니다`);
    if (zhuyiCount > 0) parts.push(`${zhuyiCount}개의 주의가 필요한 신살이 있습니다`);
    let text = parts.join(', ') + '. 명리는 자신을 아는 거울입니다. 길성의 힘을 활용하고 지혜로 불리한 요소를 화해하면 길을 부르고 흉을 피할 수 있습니다.';
    if (topJi.length > 0) text += ` 영향력이 가장 높은 길성: ${topJi.map(s => s.name).join(', ')}.`;
    if (topZhuyi.length > 0) text += ` 특히 주의해야 할 신살: ${topZhuyi.map(s => s.name).join(', ')}.`;
    return text;
  }
  const isHK = lang === 'zh-HK';
  if (jiCount === 0 && zhuyiCount === 0) {
    return isHK ? '命局神煞較少，運勢平穩，順其自然，踏實耕耘即可。' : '命局神煞较少，运势平稳，顺其自然，踏实耕耘即可。';
  }
  const parts: string[] = [];
  if (jiCount > 0) parts.push(isHK ? `命局中有${jiCount}顆吉星庇佑` : `命局中有${jiCount}颗吉星庇佑`);
  if (zhongCount > 0) parts.push(isHK ? `${zhongCount}顆中性神煞提供獨特能量` : `${zhongCount}颗中性神煞提供独特能量`);
  if (zhuyiCount > 0) parts.push(isHK ? `${zhuyiCount}顆需留意的神煞提示需調節之處` : `${zhuyiCount}颗需留意的神煞提示需调节之处`);
  let suffix = isHK
    ? '。命理是認識自我的鏡子，善用吉星之力，以智慧化解不利因素，方能趨吉避凶。'
    : '。命理是认识自我的镜子，善用吉星之力，以智慧调和不利因素，方能量力而行。';
  if (topJi.length > 0) suffix += (isHK ? ` 影響力最高的吉星：${topJi.map(s => s.name).join('、')}。` : ` 影响力最高的吉星：${topJi.map(s => s.name).join('、')}。`);
  if (topZhuyi.length > 0) suffix += (isHK ? ` 需重點關注的神煞：${topZhuyi.map(s => s.name).join('、')}。` : ` 需重点关注的神煞：${topZhuyi.map(s => s.name).join('、')}。`);
  return parts.join('，') + suffix;
}
